import { useEffect, useState } from "react";
import { X } from "lucide-react";
import api, { fmtDateTime, fmtErr, inr } from "../lib/api";

const Field = ({ label, children, mono = false }) => (
  <div>
    <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
    <dd className={`mt-0.5 break-all text-sm text-shipany-navy [overflow-wrap:anywhere] ${mono ? "mono" : "font-medium"}`}>{children || "—"}</dd>
  </div>
);

const Section = ({ title, children, testId }) => (
  <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4" data-testid={testId}>
    <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</h3>
    <div className="mt-3">{children}</div>
  </section>
);

export default function ShipmentDetailModal({ shipmentId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setDetail(null);
    setError("");
    api.get(`/shipments/${shipmentId}`)
      .then(({ data }) => setDetail(data))
      .catch((e) => setError(fmtErr(e)));
  }, [shipmentId]);

  const s = detail || {};
  const o = detail?.order || {};
  const w = detail?.warehouse || {};
  const receiverAddress = [o.delivery_address || s.delivery_address, o.delivery_address2].filter((v, i, arr) => v && arr.indexOf(v) === i).join(", ");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="shipment-detail-modal">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-bold text-shipany-navy" data-testid="shipment-detail-title">Shipment Details</h2>
            <p className="mono mt-1 text-xs text-slate-500">{s.order_number || "Loading…"} · {s.awb || ""}</p>
          </div>
          <button onClick={onClose} data-testid="shipment-detail-close" className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        {error && <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="shipment-detail-error">{error}</p>}
        {!detail && !error && <p className="py-12 text-center text-sm text-slate-400" data-testid="shipment-detail-loading">Loading full shipment details…</p>}
        {detail && (
          <div className="mt-5 grid gap-4">
            <Section title="Shipment" testId="detail-shipment-section">
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Status">{s.status}</Field><Field label="Courier">{s.courier_name}</Field>
                <Field label="Route">{s.origin_city} → {s.destination_city}</Field><Field label="Booked">{fmtDateTime(s.created_at)}</Field>
                <Field label="Payment">{s.payment_mode}</Field><Field label="COD amount">{s.payment_mode === "COD" ? inr(s.cod_amount || 0) : "—"}</Field>
                <Field label="Declared value">{s.declared_value ? inr(s.declared_value) : "—"}</Field><Field label="Charge">{inr(s.price || 0)}</Field>
              </dl>
            </Section>
            <div className="grid gap-4 lg:grid-cols-2">
              <Section title="Receiver details" testId="detail-receiver-section">
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Field label="Name">{o.delivery_name || s.delivery_name}</Field><Field label="Phone" mono>{o.delivery_phone || s.delivery_phone}</Field>
                  <Field label="Alternate phone" mono>{o.delivery_alt_phone || s.delivery_alt_phone}</Field><Field label="Email">{o.delivery_email || s.delivery_email}</Field>
                  <div className="sm:col-span-2"><Field label="Address">{receiverAddress}</Field></div>
                  <Field label="City">{o.delivery_city || s.delivery_city}</Field><Field label="State">{o.delivery_state || s.delivery_state}</Field>
                  <Field label="Pincode" mono>{o.delivery_pincode || s.delivery_pincode}</Field><Field label="Receiver GSTIN" mono>{o.receiver_gstin || s.receiver_gstin}</Field>
                  <Field label="Customer reference">{o.customer_reference || s.customer_reference}</Field><Field label="Invoice number">{o.invoice_number || s.invoice_number}</Field>
                  <Field label="E-way bill">{o.eway_bill || s.eway_bill}</Field>
                </dl>
              </Section>
              <Section title="Pickup warehouse" testId="detail-pickup-section">
                <dl className="grid gap-4 sm:grid-cols-2">
                  <Field label="Warehouse">{w.name}</Field><Field label="Contact">{w.contact_name}</Field>
                  <Field label="Phone" mono>{w.phone}</Field><Field label="Email">{w.email}</Field>
                  <div className="sm:col-span-2"><Field label="Address">{[w.address, w.city, w.state, w.pincode].filter(Boolean).join(", ")}</Field></div>
                  <Field label="GST" mono>{w.gst}</Field><Field label="Warehouse serial" mono>{w.serial ? `WH-${w.serial}` : ""}</Field>
                </dl>
              </Section>
            </div>
            <Section title="Package" testId="detail-package-section">
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <Field label="Package type">{s.package_type}</Field><Field label="Chargeable weight">{s.weight} kg</Field>
                <Field label="Boxes">{s.boxes || 1}</Field><Field label="Dimensions">{s.length ? `${s.length} × ${s.breadth} × ${s.height} cm` : "—"}</Field>
                <Field label="Reseller name">{s.reseller_name}</Field>
              </dl>
              {(s.box_details || []).length > 0 && <div className="mt-3 grid gap-2">{s.box_details.map((b, i) => <p key={i} className="rounded-lg bg-white px-3 py-2 text-xs text-slate-600" data-testid={`detail-box-${i + 1}`}>Box {i + 1}: {b.weight} kg · {b.length} × {b.breadth} × {b.height} cm</p>)}</div>}
            </Section>
            <Section title="Items" testId="detail-items-section">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400"><th className="py-2">Item</th><th>SKU</th><th>Qty</th><th>Price</th><th>Weight</th></tr></thead>
                  <tbody>{(o.items || s.items || []).map((item, i) => <tr key={i} className="border-t border-slate-200" data-testid={`detail-item-${i + 1}`}><td className="py-2 font-medium">{item.name}</td><td className="mono">{item.sku || "—"}</td><td>{item.qty}</td><td>{inr(item.price || 0)}</td><td>{item.weight ? `${item.weight} kg` : "—"}</td></tr>)}</tbody>
                </table>
              </div>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}
